/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  DataDrivenPropertyValueSpecification,
  ExpressionSpecification,
  LayerSpecification,
  type Map as MaplibreMap,
  StyleSpecification
} from "maplibre-gl";
import type {
  Feature,
  FeatureCollection,
  GeoJsonProperties,
  Geometry
} from "geojson";

import { COLOR_KEYS, COLOR_LABEL, COLORS } from "@/constants/colors";
import { CUSTOM_ORDINAL_OTHER_LEGEND_KEY } from "@/constants/dataset";
import { DEFAULT_GEOM_PRECISION } from "@/constants/map";
import { processNumericRange } from "@/helpers/numberHelpers";
import { BOUNDS_PADDING } from "@/hooks/map/interactions/useMapInteractions";
import { SpatialAggregationType } from "@/types/common";
import {
  ColorRanges,
  CustomBreaksValue,
  CustomOrdinalValue,
  DataSchemaType,
  Dataset,
  DatasetAttributeSchema,
  DatasetStyle,
  EColorLabel,
  EColorNames,
  SimpleFillValue
} from "@/types/dataset";
import { FeatureDataConfig, LayerType, MapStyleProperties } from "@/types/map";

import * as turf from "@turf/turf";

import { getClampedColor, getColorScaleMap } from "./color";
import { resolveLegendMarkerImageUrl } from "./mapLegend";

export type LayerSublayers = {
  fill?: string;
  outline?: string;
  outlineSelected?: string;
  circle?: string;
  count?: string;
  unclustered?: string;
  /** Symbol layer for the "Simple" image marker (POI). */
  icon?: string;
  line?: string;
  stroke?: string;
  label?: string;
  rank?: string;
};

// Map layers for non POI spatial type
export const NON_POI_LAYER_TYPES: (keyof LayerSublayers)[] = [
  "fill",
  "outline",
  "label",
  "rank",
  "stroke"
];

/**
 * Creates HTML tooltip content for cluster features
 */
export const createClusterTooltip = (
  pointCount: number,
  layer:
    | { paint?: { "circle-radius": number | Array<string | number> } }
    | undefined
) => {
  const paint = layer?.paint;
  const clusterRadius = paint?.["circle-radius"];
  let radiusValue = 20;

  if (Array.isArray(clusterRadius) && clusterRadius[0] === "step") {
    const steps = clusterRadius.slice(2);
    for (let i = 0; i < steps.length; i += 2) {
      const threshold = Number(steps[i]);
      const radius = Number(steps[i + 1]);
      if (i + 1 < steps.length && pointCount >= threshold) {
        radiusValue = radius;
      }
    }
  }

  return `
        <div class="tooltip-content cluster-tooltip">
          <div class="tooltip-row">
            <span class="tooltip-label">Points in cluster:</span>
            <span class="tooltip-value">${pointCount}</span>
          </div>
          <div class="tooltip-row">
            <span class="tooltip-label">Cluster radius:</span>
            <span class="tooltip-value">${radiusValue}px</span>
          </div>
          <div class="tooltip-hint">Click to expand cluster</div>
        </div>
      `;
};

/**
 * Creates HTML tooltip content for individual point features
 */
export const createFeatureTooltip = (properties: Record<string, unknown>) => {
  const excludedKeys = [
    "point_count",
    "point_count_abbreviated",
    "cluster",
    "color",
    "opacity",
    "width"
  ];

  const tooltipContent = Object.entries(properties)
    .filter(([key]) => !excludedKeys.includes(key))
    .map(([key, value]) => {
      const formattedKey = key
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
      return `
          <div class="tooltip-row">
            <span class="tooltip-label">${formattedKey}:</span>
            <span class="tooltip-value">${value}</span>
          </div>
          `;
    })
    .join("");

  return tooltipContent
    ? `<div class="tooltip-content">${tooltipContent}</div>`
    : "";
};

/**
 * Toggles the visibility of a layer and its sublayers
 */
// TODO: Tech debt might duplicate with @handleChangeVisibility src/utils/map.ts:84
// TODO: Tech debt currently time complexity is O(n) need to improve to O(1) possible  using `getLayer`
// TODO: Problem in site_profiling when toogle the visibility marker show/hide first before the layer
export const toggleLayerVisibilityById = (
  map: MaplibreMap | null,
  datasetId: string,
  isVisible: boolean,
  showLabel: boolean,
  isRankEnabled: boolean
): void => {
  if (!map) return;
  const layers = map.getStyle().layers;

  layers.forEach((layer) => {
    if (!("source" in layer) || !layer.source.startsWith(datasetId)) return;

    const isLabel = layer.id.endsWith("-label");
    const isRank = layer.id.endsWith("-rank");

    let visibility: "visible" | "none" = "none";
    if (!isLabel && !isRank) {
      visibility = isVisible ? "visible" : "none";
    } else if (isLabel) {
      visibility = showLabel && isVisible ? "visible" : "none";
    } else if (isRank) {
      visibility = isRankEnabled && isVisible ? "visible" : "none";
    }

    map.setLayoutProperty(layer.id, "visibility", visibility);
  });
};

/**
 * Generates a Maplibre "match" expression for coloring `ordinal` categories/features.
 *
 * @param colorRanges - Array of color range objects, each containing a value to match and its color.
 * @param colorFamily - The color family key to use from the `COLORS` constant.
 * @param category - The feature property name to match for coloring.
 * @returns `ExpressionSpecification` for Maplibre layer color styling, mapping each category value to its color.
 */
export function generateOrdinalColorExpression(
  colorRanges: ColorRanges[],
  colorFamily: EColorNames,
  category: string,
  step: number
): ExpressionSpecification {
  const scale = getColorScaleMap(colorFamily, step);
  return [
    "match",
    ["get", category],
    colorRanges[0]?.value ?? "",
    // Color for the first value in colorRanges
    getClampedColor(colorFamily, scale, 0),
    // Color for each remaining value in colorRanges
    ...(colorRanges.length
      ? colorRanges
          .slice(1)
          .flatMap((range, index) => [
            range.value ? range.value : "",
            range.value
              ? getClampedColor(colorFamily, scale, index + 1)
              : "#CECECE"
          ])
      : [" ", getClampedColor(colorFamily, scale, scale.length - 1)]),
    // Fallback color if no match is found
    getClampedColor(colorFamily, scale, scale.length - 1)
  ];
}

export const addPolygonLayer = (
  map: MaplibreMap | null,
  sourceId: string,
  style?: MapStyleProperties | undefined
): void => {
  if (!map) return;

  map.addLayer({
    id: `${sourceId}-fill`,
    type: "fill",
    source: sourceId,
    layout: {},
    paint: {
      "fill-color": style?.fillColor ?? "rgba(136, 136, 136, 0.4)",
      "fill-opacity": [
        "case",
        [
          "any",
          ["boolean", ["feature-state", "hover"], false],
          ["boolean", ["feature-state", "selected"], false]
        ],
        0.6, // Hover opacity
        style?.fillOpacity || 0.9 // Default opacity
      ]
    }
  });
};

export const addPolygonOutlineLayer = (
  map: MaplibreMap | null,
  sourceId: string,
  style?: MapStyleProperties | undefined
): void => {
  if (!map) return;

  map.addLayer({
    id: `${sourceId}-stroke`,
    type: "line",
    source: sourceId,
    layout: {},
    paint: {
      "line-color": style?.lineColor ?? "#088C7A",
      "line-width": style?.lineWidth ?? 2,
      "line-dasharray": [4, 4],
      "line-opacity": [
        "case",
        [
          "any",
          ["boolean", ["feature-state", "hover"], false],
          ["boolean", ["feature-state", "selected"], false]
        ],
        1,
        style?.opacity ?? 1
      ]
    }
  });
};

export const addRoutingLayer = (
  map: MaplibreMap | null,
  sourceId: string,
  style?: MapStyleProperties | undefined,
  /**
   * Existing layer id to insert the routing/circle layers below (keeps them
   * under the catchment marker layers).
   */
  beforeId?: string
): void => {
  if (!map) return;
  const beforeLayerId =
    beforeId && map.getLayer(beforeId) ? beforeId : undefined;
  map.addLayer(
    {
      id: `${sourceId}-fill-routing`,
      type: "fill",
      source: sourceId,
      paint: {
        "fill-color": style?.fillColor ?? "rgba(75, 197, 9, 0.2)"
      }
    },
    beforeLayerId
  );
  map.addLayer(
    {
      id: `${sourceId}-line`,
      type: "line",
      source: sourceId,
      paint: {
        "line-color": style?.lineColor ?? "rgba(75, 197, 9, 1)",
        "line-width": style?.lineWidth ?? 2
      }
    },
    beforeLayerId
  );
};

export const addCircleRadiusLayer = (
  map: MaplibreMap | null,
  sourceId: string,
  style?: MapStyleProperties | undefined
): void => {
  if (!map) return;
  map.addLayer({
    id: `${sourceId}-fill-circle-radius`,
    type: "fill",
    source: sourceId,
    paint: {
      "fill-color": style?.fillColor ?? "rgba(75, 197, 9, 0.4)",
      "fill-opacity": style?.fillOpacity ?? 0.4
    }
  });
  map.addLayer({
    id: `${sourceId}-line`,
    type: "line",
    source: sourceId,
    paint: {
      "line-color": style?.lineColor ?? "rgba(75, 197, 9, 1)",
      "line-width": style?.lineWidth ?? 2
    }
  });
};

export const removeRegionPolygonSource = (
  map: MaplibreMap | null,
  datasetId: string
): void => {
  if (!map) return;
  if (map.getLayer(`${datasetId}-fill`)) map.removeLayer(`${datasetId}-fill`);
  if (map.getLayer(`${datasetId}-line`)) map.removeLayer(`${datasetId}-line`);
  if (map.getSource(datasetId)) map.removeSource(datasetId);
};

export const changeColorOrdinalLayer = (
  map: MaplibreMap | null,
  sublayers: LayerSublayers,
  colorFamily: EColorNames,
  colorRanges: ColorRanges[],
  selectedCategory: string,
  step: number
) => {
  if (!map) return;

  // Create ordinal color expression using match
  const ordinalColorExpression = generateOrdinalColorExpression(
    colorRanges,
    colorFamily,
    selectedCategory,
    step
  );

  const colorExpression: ExpressionSpecification = [
    "case",
    ["has", selectedCategory],
    ordinalColorExpression,
    "#CECECE"
  ];

  changeMapPaintPropertySubLayer(
    map,
    sublayers,
    colorFamily,
    step,
    colorExpression
  );
};

export const changeColorQuantileLayer = (
  map: MaplibreMap | null,
  sublayers: LayerSublayers,
  colorFamily: EColorNames,
  colorRanges: DatasetStyle["colorRanges"],
  selectedCategory: string,
  step: number
) => {
  if (!map) return;

  // Handle case when colorRanges has only one item
  if (colorRanges.length === 1) {
    const singleColor =
      COLORS[colorFamily][COLOR_KEYS[0]] || colorRanges[0]?.color;

    const colorExpression = [
      "case",
      ["==", ["get", selectedCategory], null],
      "#CECECE",
      ["has", selectedCategory],
      singleColor,
      "#CECECE" // Default fallback color
    ];

    changeMapPaintPropertySubLayer(
      map,
      sublayers,
      colorFamily,
      step,
      colorExpression
    );

    return;
  }

  // Create an array of unique breakpoints and their colors
  const uniqueStops = colorRanges
    .filter((range) => range.min != null) // skip null min values
    .reduce((acc: [number, string][], range, index) => {
      if (!acc.some(([value]) => value === range.min)) {
        acc.push([
          range.min!,
          COLORS[colorFamily][getColorScaleMap(colorFamily, step)[index]]
        ]);
      }
      return acc;
    }, [])
    .sort(([a], [b]) => a - b);

  // Handle case when uniqueStops has less than 2 entries (not enough for a valid step expression)
  if (uniqueStops.length < 2) {
    // If no ranges or just one range, use a default or the single color
    const fillColor =
      uniqueStops.length === 1
        ? uniqueStops[0][1]
        : COLORS[colorFamily][getColorScaleMap(colorFamily, step)[0]];

    const colorExpression = [
      "case",
      ["has", selectedCategory],
      fillColor,
      "#CECECE" // Default fallback color
    ];

    changeMapPaintPropertySubLayer(
      map,
      sublayers,
      colorFamily,
      step,
      colorExpression
    );

    return;
  }

  // We have at least 2 entries, so we can create a valid step expression
  const stepExpression = [
    "step",
    ["get", selectedCategory],
    uniqueStops[0][1], // Default color for values below first threshold
    uniqueStops[1][0],
    uniqueStops[1][1], // First threshold and color
    ...uniqueStops.slice(2).flatMap(([value, color]) => [value, color])
  ];

  const colorExpression = [
    "case",
    ["==", ["get", selectedCategory], null],
    "#CECECE",
    ["has", selectedCategory],
    stepExpression,
    "#CECECE"
  ];

  changeMapPaintPropertySubLayer(
    map,
    sublayers,
    colorFamily,
    step,
    colorExpression
  );
};

// Image markers are pre-rendered into a fixed 36 device px circle (≈18 CSS px
// @ pixelRatio 2), so `icon-size` stays 1.
const SIMPLE_MARKER_ICON_SIZE = 1;
const SIMPLE_MARKER_DIAMETER = 36;

// Selected-state yellow ring (drawn by the hidden dot's stroke): radius 10 with
// a 2px stroke hugs the ~18px image marker's edge with no gap.
const SIMPLE_MARKER_SELECT_RADIUS = 10;
const SIMPLE_MARKER_SELECT_STROKE_WIDTH = 2;
// Icon markers render at the same ~18 CSS px diameter as image markers
// (legendIcons.tsx MARKER_RENDER_SIZE), so they share the same select radius.
const ICON_MARKER_SELECT_RADIUS = SIMPLE_MARKER_SELECT_RADIUS;
const DEFAULT_POINT_RADIUS: ExpressionSpecification = [
  "case",
  ["boolean", ["feature-state", "hover"], false],
  8,
  6
];
const SELECTED_RING_OPACITY: ExpressionSpecification = [
  "case",
  ["boolean", ["feature-state", "selected"], false],
  1,
  0
];

/**
 * Draws an image cover-fit into a circle of `SIMPLE_MARKER_DIAMETER` and returns
 * it as a PNG data URL (so it can be registered as a reliable image element).
 * Returns null if a 2D context / encoding isn't available.
 */
const buildCircularMarkerDataUrl = (img: HTMLImageElement): string | null => {
  const canvas = document.createElement("canvas");
  canvas.width = SIMPLE_MARKER_DIAMETER;
  canvas.height = SIMPLE_MARKER_DIAMETER;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  const radius = SIMPLE_MARKER_DIAMETER / 2;
  ctx.beginPath();
  ctx.arc(radius, radius, radius, 0, Math.PI * 2);
  ctx.closePath();
  ctx.clip();

  const srcW = img.width || SIMPLE_MARKER_DIAMETER;
  const srcH = img.height || SIMPLE_MARKER_DIAMETER;
  const scale = Math.max(
    SIMPLE_MARKER_DIAMETER / srcW,
    SIMPLE_MARKER_DIAMETER / srcH
  );
  const w = srcW * scale;
  const h = srcH * scale;
  ctx.drawImage(
    img,
    (SIMPLE_MARKER_DIAMETER - w) / 2,
    (SIMPLE_MARKER_DIAMETER - h) / 2,
    w,
    h
  );

  try {
    return canvas.toDataURL("image/png");
  } catch {
    return null;
  }
};

/**
 * Registers `dataUrl` as a round map marker under `imageId` (re-registering if it
 * exists), then calls `onReady` — or `onError` (nothing registered) if it fails to
 * load. `skipRounding` skips the circular normalization for pre-rounded icon markers.
 */
export const registerMapImage = (
  map: MaplibreMap | null,
  imageId: string,
  dataUrl: string,
  onReady?: () => void,
  onError?: () => void,
  skipRounding: boolean = false
) => {
  if (!map || typeof map.addImage !== "function") return;

  const register = (image: HTMLImageElement) => {
    try {
      if (map.hasImage?.(imageId)) map.removeImage(imageId);
      map.addImage(imageId, image, { pixelRatio: 2 });
      onReady?.();
    } catch {
      onError?.();
    }
  };

  const img = new Image();

  if (!dataUrl.startsWith("data:")) img.crossOrigin = "anonymous";
  img.onload = () => {
    if (skipRounding) {
      register(img);
      return;
    }
    const roundedUrl = buildCircularMarkerDataUrl(img);
    if (!roundedUrl) {
      register(img);
      return;
    }
    const rounded = new Image();
    rounded.onload = () => register(rounded);
    rounded.onerror = () => register(img);
    rounded.src = roundedUrl;
  };
  // The source image failed to load — nothing is registered, so let the caller
  // fall back (e.g. keep the colored dot) instead of running the ready path.
  img.onerror = () => onError?.();
  img.src = dataUrl;
};

/**
 * Ensures the POI image-marker symbol layer exists, recreating it from the
 * unclustered point layer's source when a non-marker build path skipped it.
 */
const ensureSymbolLayer = (map: MaplibreMap, sublayers: LayerSublayers) => {
  const { icon, unclustered } = sublayers;
  if (!icon || map.getLayer(icon)) return;
  const sibling = unclustered && map.getLayer(unclustered);
  // `getLayer` returns a union whose `source` isn't surfaced on every member;
  // for our GeoJSON point layers it's always the string source id. Safe narrow.
  const sourceId = sibling
    ? (sibling as unknown as { source?: string }).source
    : undefined;
  if (!sourceId) return;
  map.addLayer({
    id: icon,
    type: "symbol",
    source: sourceId,
    filter: ["!", ["has", "point_count"]],
    layout: {
      "icon-allow-overlap": true,
      "icon-size": SIMPLE_MARKER_ICON_SIZE,
      visibility: "none"
    }
  });
};

/**
 * Toggles a Simple POI layer between the flat dot and a marker: with `markerUrl`
 * the marker replaces the dot (the hidden circle keeps a yellow selected-only
 * ring); without one, the icon layer is hidden and the dot restored.
 */
const applySimpleMarker = (
  map: MaplibreMap | null,
  sublayers: LayerSublayers,
  fillOpacity: number,
  markerUrl: string | null,
  /** True when `markerUrl` is a generated icon marker — registered as-is
   *  (`skipRounding`) and framed by the larger selected ring. */
  isIconMarker: boolean = false
) => {
  if (!map) return;
  const { icon, unclustered } = sublayers;
  const hasUnclustered = unclustered && map.getLayer(unclustered);

  if (markerUrl) {
    if (hasUnclustered) {
      // Hide the dot fill; keep a yellow ring that frames the marker only while
      // the feature is selected/active.
      map.setPaintProperty(unclustered, "circle-opacity", 0);
      map.setPaintProperty(
        unclustered,
        "circle-radius",
        isIconMarker ? ICON_MARKER_SELECT_RADIUS : SIMPLE_MARKER_SELECT_RADIUS
      );
      map.setPaintProperty(unclustered, "circle-stroke-color", "#FFCF01");
      map.setPaintProperty(
        unclustered,
        "circle-stroke-width",
        SIMPLE_MARKER_SELECT_STROKE_WIDTH
      );
      map.setPaintProperty(
        unclustered,
        "circle-stroke-opacity",
        SELECTED_RING_OPACITY
      );
    }
    if (!icon) return;
    // Recreate the symbol layer if a non-marker build path skipped it, so the
    // marker renders on a live config change without a full layer rebuild.
    ensureSymbolLayer(map, sublayers);
    const imageId = `${icon}-image`;
    registerMapImage(
      map,
      imageId,
      markerUrl,
      () => {
        if (!map.getLayer(icon)) return;
        map.setLayoutProperty(icon, "icon-image", imageId);
        map.setLayoutProperty(icon, "visibility", "visible");
        // Open the marker at the layer's current opacity (the slider keeps it
        // in sync afterwards via `changeOpacityLayer`).
        map.setPaintProperty(icon, "icon-opacity", fillOpacity);
      },
      // Marker failed to load → fall back to the flat colored dot (hidden above
      // in anticipation of the marker), instead of a blank marker.
      () => applySimpleMarker(map, sublayers, fillOpacity, null),
      isIconMarker
    );
    return;
  }

  // No marker: hide the icon layer and bring the flat dot back to its defaults
  // (the selected stroke color is re-applied by `changeColorSimpleLayer`).
  if (icon && map.getLayer(icon)) {
    map.setLayoutProperty(icon, "visibility", "none");
  }
  if (hasUnclustered) {
    map.setPaintProperty(unclustered, "circle-opacity", fillOpacity);
    map.setPaintProperty(unclustered, "circle-radius", DEFAULT_POINT_RADIUS);
    map.setPaintProperty(unclustered, "circle-stroke-width", 1);
    map.setPaintProperty(unclustered, "circle-stroke-opacity", 1);
  }
};

/**
 * Resets a POI layer to default point styling — hides the image marker and
 * restores the dot. Call before repainting with a value-based scale so the
 * markers and hidden dots don't linger.
 */
export const resetSimpleMarker = (
  map: MaplibreMap | null,
  sublayers: LayerSublayers,
  fillOpacity: number = 0.7
) => {
  if (!map) return;
  const { icon, unclustered } = sublayers;

  if (icon && map.getLayer(icon)) {
    map.setLayoutProperty(icon, "visibility", "none");
  }
  if (unclustered && map.getLayer(unclustered)) {
    map.setPaintProperty(unclustered, "circle-opacity", fillOpacity);
    map.setPaintProperty(unclustered, "circle-radius", DEFAULT_POINT_RADIUS);
    map.setPaintProperty(unclustered, "circle-stroke-width", 1);
    map.setPaintProperty(unclustered, "circle-stroke-opacity", 1);
    map.setPaintProperty(unclustered, "circle-stroke-color", [
      "case",
      ["boolean", ["feature-state", "selected"], false],
      "#FFCF01",
      "#ffffff"
    ]);
  }
};

/**
 * Paints a line dataset's stroke color. Lines have no fill layer, so their color
 * lives on the stroke sublayer; a no-op for geometries without one.
 */
const setLineStrokeColor = (
  map: MaplibreMap,
  sublayers: LayerSublayers,
  color: string | ExpressionSpecification
) => {
  if (sublayers.stroke && map.getLayer(sublayers.stroke)) {
    map.setPaintProperty(sublayers.stroke, "line-color", color);
  }
};

/**
 * Paints per-feature fill + outline across every layer type (cluster, points,
 * polygon fill/outline, line stroke). Shared by Custom Ordinal & Custom Breaks.
 */
const applyPerFeatureColorExpressions = (
  map: MaplibreMap,
  sublayers: LayerSublayers,
  fillExpression: string | ExpressionSpecification,
  outlineExpression: string | ExpressionSpecification,
  clusterColor: string
) => {
  // Cluster bubbles aggregate mixed features — paint them a single color.
  if (sublayers.circle && map.getLayer(sublayers.circle)) {
    map.setPaintProperty(sublayers.circle, "circle-color", clusterColor);
  }
  // Unclustered points: per-value fill + outline (keeping the selected ring).
  if (sublayers.unclustered && map.getLayer(sublayers.unclustered)) {
    map.setPaintProperty(sublayers.unclustered, "circle-color", fillExpression);
    map.setPaintProperty(sublayers.unclustered, "circle-stroke-color", [
      "case",
      ["boolean", ["feature-state", "selected"], false],
      "#FFCF01",
      outlineExpression
    ] as unknown as ExpressionSpecification);
  }
  // Fill layers (polygon, administrative, h3, geohash): per-value fill + outline.
  if (sublayers.fill && map.getLayer(sublayers.fill)) {
    map.setPaintProperty(sublayers.fill, "fill-color", fillExpression);
  }
  if (sublayers.outline && map.getLayer(sublayers.outline)) {
    map.setPaintProperty(sublayers.outline, "line-color", outlineExpression);
  }
  // Line layers have no fill layer — the stroke line takes the fill expression.
  setLineStrokeColor(map, sublayers, fillExpression);
};

/**
 * Repaints a "Simple" Color Scale layer (POI points): a single flat fill +
 * outline, or — when an image is set — the uploaded image marker. Used for
 * real-time updates when the user edits the Simple config or switches to it.
 */
export const changeColorSimpleLayer = (
  map: MaplibreMap | null,
  sublayers: LayerSublayers,
  simpleConfig: Pick<
    SimpleFillValue,
    "fillColor" | "outlineColor" | "imageUrl"
  > &
    Partial<Pick<SimpleFillValue, "iconId">>,
  fillOpacity: number = 0.7
) => {
  if (!map) return;

  // The icon marker or uploaded image to draw; null keeps the colored dot.
  const markerImageUrl = resolveLegendMarkerImageUrl(simpleConfig);

  // Clustered bubbles take the flat fill, but teal in image mode (where the
  // fill color is hidden/irrelevant).
  if (sublayers.circle && map.getLayer(sublayers.circle)) {
    map.setPaintProperty(
      sublayers.circle,
      "circle-color",
      simpleConfig.imageUrl ? COLORS.TEAL[500] : simpleConfig.fillColor
    );
  }

  // Unclustered points take the flat fill + outline (keeping the selected ring).
  if (sublayers.unclustered && map.getLayer(sublayers.unclustered)) {
    map.setPaintProperty(
      sublayers.unclustered,
      "circle-color",
      simpleConfig.fillColor
    );
    map.setPaintProperty(sublayers.unclustered, "circle-stroke-color", [
      "case",
      ["boolean", ["feature-state", "selected"], false],
      "#FFCF01",
      simpleConfig.outlineColor
    ]);
  }

  // Fill layers (polygon, administrative, h3, geohash): flat fill + outline.
  if (sublayers.fill && map.getLayer(sublayers.fill)) {
    map.setPaintProperty(sublayers.fill, "fill-color", simpleConfig.fillColor);
  }
  if (sublayers.outline && map.getLayer(sublayers.outline)) {
    map.setPaintProperty(
      sublayers.outline,
      "line-color",
      simpleConfig.outlineColor
    );
  }

  // Line layers have no fill layer — the stroke line takes the flat fill color.
  setLineStrokeColor(map, sublayers, simpleConfig.fillColor);

  applySimpleMarker(
    map,
    sublayers,
    fillOpacity,
    markerImageUrl,
    !!simpleConfig.iconId
  );
};

/**
 * Builds the Custom Ordinal `match` color expression: assigned values map to their
 * category color (duplicates dropped, first wins), everything else — or an empty
 * assignment — falls back to the flat `otherColor`.
 */
export function buildCustomOrdinalColorExpression(
  steps: { values: string[]; color: string }[],
  otherColor: string,
  attribute: string
): ExpressionSpecification | string {
  const pairs: string[] = [];
  const seen = new Set<string>();
  steps.forEach((step) => {
    step.values.forEach((value) => {
      if (seen.has(value)) return;
      seen.add(value);
      pairs.push(value, step.color);
    });
  });
  if (!pairs.length) return otherColor;
  // MapLibre's ExpressionSpecification is a deep tuple union it can't infer from
  // a spread-built array; the runtime shape is a valid `match` expression. This
  // cast (and the others in this file) is the standard escape for that.
  return [
    "match",
    // `to-string` lets numeric columns match the string category values.
    ["to-string", ["get", attribute]],
    ...pairs,
    otherColor
  ] as unknown as ExpressionSpecification;
}

/**
 * Per-category markers for a Custom Ordinal POI layer — `applySimpleMarker`
 * extended to multiple categories via `match` expressions on the attribute
 * value, so color-only categories (and "Other") keep their colored dot.
 */
export const applyCustomOrdinalMarkers = (
  map: MaplibreMap | null,
  sublayers: LayerSublayers,
  config: CustomOrdinalValue,
  attribute: string,
  fillOpacity: number
) => {
  if (!map) return;
  const { icon, unclustered } = sublayers;

  // Steps that draw a marker: an uploaded image or a selected icon.
  const markerSteps = config.steps.filter(
    (step) => resolveLegendMarkerImageUrl(step) !== null
  );

  // No category draws a marker: hide the symbol layer and restore the dots
  // (per-value colors are set by the caller).
  if (!markerSteps.length || !icon) {
    if (icon && map.getLayer(icon)) {
      map.setLayoutProperty(icon, "visibility", "none");
    }
    if (unclustered && map.getLayer(unclustered)) {
      map.setPaintProperty(unclustered, "circle-opacity", fillOpacity);
      map.setPaintProperty(unclustered, "circle-radius", DEFAULT_POINT_RADIUS);
      map.setPaintProperty(unclustered, "circle-stroke-width", 1);
      map.setPaintProperty(unclustered, "circle-stroke-opacity", 1);
    }
    return;
  }

  // A feature may carry either the display name or the raw value, so match on
  // both keys (mirrors `changeColorCustomOrdinalLayer`'s `expandKeys`).
  const lookup = config.valueByDisplayName ?? {};
  const expandKeys = (values: string[]) =>
    values.flatMap((v) => {
      const raw = lookup[v];
      return raw != null && raw !== v ? [v, raw] : [v];
    });

  // (value -> map image id) per marker category, deduped (first category
  // wins). Icon-marker values are tracked separately — their selected ring
  // needs a larger radius than image markers'.
  const valueImageId = new Map<string, string>();
  const iconMarkerValues = new Set<string>();
  markerSteps.forEach((step, index) => {
    const imageId = `${icon}-ordinal-${index}`;
    expandKeys(step.values).forEach((value) => {
      if (valueImageId.has(value)) return;
      valueImageId.set(value, imageId);
      if (step.iconId) iconMarkerValues.add(value);
    });
  });

  // Ensure the symbol layer exists. On a live save the POI layer isn't rebuilt,
  // so if it was created under a method that doesn't add the marker layer
  // (ordinal/quantile/preview), there'd be nothing to draw the images on.
  ensureSymbolLayer(map, sublayers);

  // (Re)build the value-driven expressions from the map images actually
  // registered, so `icon-image` never points at a missing sprite (MapLibre
  // warns and drops it) and a failed marker keeps its colored dot.
  const apply = () => {
    if (!map.getLayer(icon)) return;
    const iconImagePairs: (string | number)[] = [];
    const readyValues: string[] = [];
    valueImageId.forEach((imageId, value) => {
      if (!map.hasImage?.(imageId)) return;
      iconImagePairs.push(value, imageId);
      readyValues.push(value);
    });

    if (unclustered && map.getLayer(unclustered)) {
      // Hide the dot fill for marker features (keep it for dot-only ones)
      // and turn their stroke into the selected-only yellow ring, sized per
      // marker kind so it hugs the marker's edge.
      const byValue = (
        imageMarkerOutput: number | ExpressionSpecification,
        dotOutput: number | ExpressionSpecification,
        iconMarkerOutput: number | ExpressionSpecification = imageMarkerOutput
      ) =>
        readyValues.length
          ? ([
              "match",
              ["to-string", ["get", attribute]],
              ...readyValues.flatMap((v) => [
                v,
                iconMarkerValues.has(v) ? iconMarkerOutput : imageMarkerOutput
              ]),
              dotOutput
            ] as unknown as ExpressionSpecification)
          : dotOutput;

      map.setPaintProperty(
        unclustered,
        "circle-opacity",
        byValue(0, fillOpacity)
      );
      map.setPaintProperty(
        unclustered,
        "circle-radius",
        byValue(
          SIMPLE_MARKER_SELECT_RADIUS,
          DEFAULT_POINT_RADIUS,
          ICON_MARKER_SELECT_RADIUS
        )
      );
      map.setPaintProperty(
        unclustered,
        "circle-stroke-width",
        byValue(SIMPLE_MARKER_SELECT_STROKE_WIDTH, 1)
      );
      map.setPaintProperty(
        unclustered,
        "circle-stroke-opacity",
        byValue(SELECTED_RING_OPACITY, 1)
      );
    }

    if (!iconImagePairs.length) {
      map.setLayoutProperty(icon, "visibility", "none");
      return;
    }
    map.setLayoutProperty(icon, "icon-image", [
      "match",
      ["to-string", ["get", attribute]],
      ...iconImagePairs,
      // Unmatched features render no icon (keeps their colored dot).
      ""
    ] as unknown as ExpressionSpecification);
    map.setLayoutProperty(icon, "visibility", "visible");
    map.setPaintProperty(icon, "icon-opacity", fillOpacity);
  };

  // Register each category image, re-applying as each one becomes ready, and
  // run once now for any image already registered (re-renders, reloads).
  markerSteps.forEach((step, index) => {
    registerMapImage(
      map,
      `${icon}-ordinal-${index}`,
      resolveLegendMarkerImageUrl(step) as string,
      apply,
      undefined,
      // Icon markers register as-is at their display size.
      !!step.iconId
    );
  });
  apply();
};

/**
 * Repaints a "Custom Ordinal" layer (points or polygons): each feature takes the
 * fill/outline of the category its attribute value belongs to, or the "Other"
 * colors when the value isn't assigned. Used for real-time updates when the user
 * saves the Custom Ordinal config or switches the layer to it.
 */
export const changeColorCustomOrdinalLayer = (
  map: MaplibreMap | null,
  sublayers: LayerSublayers,
  config: CustomOrdinalValue,
  attribute: string,
  fillOpacity: number = 0.7
) => {
  if (!map) return;

  // The config panel works in display-name space, but a feature may carry either
  // the display name or the raw value (these differ for coded attributes). Match
  // on BOTH keys for each assigned value, so whichever the feature actually
  // holds is colored — instead of silently falling through to "Other".
  const lookup = config.valueByDisplayName ?? {};
  const expandKeys = (values: string[]) =>
    values.flatMap((v) => {
      const raw = lookup[v];
      return raw != null && raw !== v ? [v, raw] : [v];
    });

  const fillExpression = buildCustomOrdinalColorExpression(
    config.steps.map((s) => ({
      values: expandKeys(s.values),
      color: s.fillColor
    })),
    config.otherFillColor,
    attribute
  );
  const outlineExpression = buildCustomOrdinalColorExpression(
    config.steps.map((s) => ({
      values: expandKeys(s.values),
      color: s.outlineColor
    })),
    config.otherOutlineColor,
    attribute
  );

  // Cluster bubbles take a single representative color (first category → "Other").
  const clusterColor = config.steps[0]?.fillColor ?? config.otherFillColor;
  applyPerFeatureColorExpressions(
    map,
    sublayers,
    fillExpression,
    outlineExpression,
    clusterColor
  );

  // POI image markers: show each category's uploaded image (or restore the dots
  // when none use an image). Applied last so it layers over the per-value paint
  // above — mirroring how `changeColorSimpleLayer` ends with `applySimpleMarker`.
  applyCustomOrdinalMarkers(map, sublayers, config, attribute, fillOpacity);
};

/**
 * MapLibre condition for a Custom Breaks range — half-open `(start, end]`; a
 * `null` bound is unbounded on that side (both `null` matches everything).
 */
function buildBreakCondition(
  step: { start: number | null; end: number | null },
  attribute: string
): ExpressionSpecification | boolean {
  const value = ["to-number", ["get", attribute]];
  const conds: unknown[] = [];
  if (step.start !== null) conds.push([">", value, step.start]);
  if (step.end !== null) conds.push(["<=", value, step.end]);
  if (!conds.length) return true;
  if (conds.length === 1) return conds[0] as ExpressionSpecification;
  return ["all", ...conds] as unknown as ExpressionSpecification;
}

/**
 * Builds the Custom Breaks `case` color expression: each range paints its color;
 * gaps and missing/null values fall back to `otherColor`. `case` (not `step`)
 * keeps boundaries half-open and lets manual-edit gaps route to "Other".
 */
export function buildCustomBreaksColorExpression(
  steps: { start: number | null; end: number | null; color: string }[],
  otherColor: string,
  attribute: string
): ExpressionSpecification | string {
  if (!steps.length) return otherColor;
  const cases: unknown[] = [
    ["!", ["has", attribute]],
    otherColor,
    ["==", ["get", attribute], null],
    otherColor
  ];
  steps.forEach((step) => {
    cases.push(buildBreakCondition(step, attribute), step.color);
  });
  return ["case", ...cases, otherColor] as unknown as ExpressionSpecification;
}

/**
 * Per-range markers for a Custom Breaks POI layer — the breaks analogue of
 * `applyCustomOrdinalMarkers`, using `case` expressions on the numeric value,
 * so ranges without a marker keep their colored dot.
 */
export const applyCustomBreaksMarkers = (
  map: MaplibreMap | null,
  sublayers: LayerSublayers,
  config: CustomBreaksValue,
  attribute: string,
  fillOpacity: number
) => {
  if (!map) return;
  const { icon, unclustered } = sublayers;

  const markerSteps = config.steps
    .map((step, index) => ({ step, index }))
    .filter(({ step }) => resolveLegendMarkerImageUrl(step) !== null);

  if (!markerSteps.length || !icon) {
    if (icon && map.getLayer(icon)) {
      map.setLayoutProperty(icon, "visibility", "none");
    }
    if (unclustered && map.getLayer(unclustered)) {
      map.setPaintProperty(unclustered, "circle-opacity", fillOpacity);
      map.setPaintProperty(unclustered, "circle-radius", DEFAULT_POINT_RADIUS);
      map.setPaintProperty(unclustered, "circle-stroke-width", 1);
      map.setPaintProperty(unclustered, "circle-stroke-opacity", 1);
    }
    return;
  }

  ensureSymbolLayer(map, sublayers);

  const apply = () => {
    if (!map.getLayer(icon)) return;
    const iconImageCases: unknown[] = [];
    // Each ready range records its marker kind so the selected ring radius
    // matches that marker's size.
    const readyConds: {
      cond: ExpressionSpecification | boolean;
      isIconMarker: boolean;
    }[] = [];
    markerSteps.forEach(({ step, index }) => {
      const imageId = `${icon}-break-${index}`;
      if (!map.hasImage?.(imageId)) return;
      const cond = buildBreakCondition(step, attribute);
      iconImageCases.push(cond, imageId);
      readyConds.push({ cond, isIconMarker: !!step.iconId });
    });

    if (unclustered && map.getLayer(unclustered)) {
      const byCond = (
        imageMarkerOutput: number | ExpressionSpecification,
        dotOutput: number | ExpressionSpecification,
        iconMarkerOutput: number | ExpressionSpecification = imageMarkerOutput
      ): number | ExpressionSpecification =>
        readyConds.length
          ? ([
              "case",
              ...readyConds.flatMap(({ cond, isIconMarker }) => [
                cond,
                isIconMarker ? iconMarkerOutput : imageMarkerOutput
              ]),
              dotOutput
            ] as unknown as ExpressionSpecification)
          : dotOutput;

      map.setPaintProperty(
        unclustered,
        "circle-opacity",
        byCond(0, fillOpacity)
      );
      map.setPaintProperty(
        unclustered,
        "circle-radius",
        byCond(
          SIMPLE_MARKER_SELECT_RADIUS,
          DEFAULT_POINT_RADIUS,
          ICON_MARKER_SELECT_RADIUS
        )
      );
      map.setPaintProperty(
        unclustered,
        "circle-stroke-width",
        byCond(SIMPLE_MARKER_SELECT_STROKE_WIDTH, 1)
      );
      map.setPaintProperty(
        unclustered,
        "circle-stroke-opacity",
        byCond(SELECTED_RING_OPACITY, 1)
      );
    }

    if (!iconImageCases.length) {
      map.setLayoutProperty(icon, "visibility", "none");
      return;
    }
    map.setLayoutProperty(icon, "icon-image", [
      "case",
      ...iconImageCases,
      ""
    ] as unknown as ExpressionSpecification);
    map.setLayoutProperty(icon, "visibility", "visible");
    map.setPaintProperty(icon, "icon-opacity", fillOpacity);
  };

  markerSteps.forEach(({ step, index }) => {
    registerMapImage(
      map,
      `${icon}-break-${index}`,
      resolveLegendMarkerImageUrl(step) as string,
      apply,
      undefined,
      // Icon markers register as-is at their display size.
      !!step.iconId
    );
  });
  apply();
};

/**
 * Repaints a Custom Breaks layer: each feature takes the fill/outline of the
 * numeric range its attribute value falls into, or the "Other" colors outside
 * every range. Used for real-time updates on config save/switch.
 */
export const changeColorCustomBreaksLayer = (
  map: MaplibreMap | null,
  sublayers: LayerSublayers,
  config: CustomBreaksValue,
  attribute: string,
  fillOpacity: number = 0.7
) => {
  if (!map) return;

  const fillExpression = buildCustomBreaksColorExpression(
    config.steps.map((s) => ({
      start: s.start,
      end: s.end,
      color: s.fillColor
    })),
    config.otherFillColor,
    attribute
  );
  const outlineExpression = buildCustomBreaksColorExpression(
    config.steps.map((s) => ({
      start: s.start,
      end: s.end,
      color: s.outlineColor
    })),
    config.otherOutlineColor,
    attribute
  );

  // Cluster bubbles take the first range's color (falling back to "Other").
  const clusterColor = config.steps[0]?.fillColor ?? config.otherFillColor;
  applyPerFeatureColorExpressions(
    map,
    sublayers,
    fillExpression,
    outlineExpression,
    clusterColor
  );

  applyCustomBreaksMarkers(map, sublayers, config, attribute, fillOpacity);
};

/**
 * Adds the (initially hidden) symbol layer used for Simple image markers and
 * applies the current marker state. Called once when the POI layer is created.
 */
export const addSimpleMarkerLayer = (
  map: MaplibreMap | null,
  sourceId: string,
  sublayers: LayerSublayers,
  fillOpacity: number,
  /** The Simple config whose marker (icon or uploaded image) to apply. */
  simpleConfig: Pick<
    SimpleFillValue,
    "imageUrl" | "iconId" | "fillColor"
  > | null,
  beforeId?: string
) => {
  if (!map || !sublayers.icon) return;

  if (!map.getLayer(sublayers.icon)) {
    map.addLayer(
      {
        id: sublayers.icon,
        type: "symbol",
        source: sourceId,
        filter: ["!", ["has", "point_count"]],
        layout: {
          "icon-allow-overlap": true,
          "icon-size": SIMPLE_MARKER_ICON_SIZE,
          visibility: "none"
        }
      },
      beforeId
    );
  }

  applySimpleMarker(
    map,
    sublayers,
    fillOpacity,
    resolveLegendMarkerImageUrl(simpleConfig),
    !!simpleConfig?.iconId
  );
};

const changeMapPaintPropertySubLayer = (
  map: MaplibreMap | null,
  sublayers: LayerSublayers,
  colorFamily: EColorNames,
  step: number,
  colorExpression:
    | ExpressionSpecification
    | (string | (string | string[] | null)[] | (string | number | string[])[])[]
) => {
  if (!map) return;
  // Handle polygon layers
  if (sublayers.fill && map.getLayer(sublayers.fill)) {
    map.setPaintProperty(sublayers.fill, "fill-color", colorExpression);
  }

  // Reset the polygon/fill OUTLINE to the value-based default (flat white),
  // clearing any per-category outline left behind by Custom Ordinal — otherwise
  // the stroke stays "stuck" on the previous Custom Ordinal colors after
  // switching back to Ordinal/Quantile. Selection is drawn by the separate
  // `outlineSelected` layer, so the base outline is a plain color.
  if (sublayers.outline && map.getLayer(sublayers.outline)) {
    map.setPaintProperty(sublayers.outline, "line-color", "#ffffff");
  }

  // Handle point layers
  if (
    sublayers.circle &&
    sublayers.unclustered &&
    map.getLayer(sublayers.circle) &&
    map.getLayer(sublayers.unclustered)
  ) {
    // Update clustered points
    map.setPaintProperty(
      sublayers.circle,
      "circle-color",
      COLORS[colorFamily][getColorScaleMap(colorFamily, step)[0]]
    );

    // Update unclustered points
    map.setPaintProperty(
      sublayers.unclustered,
      "circle-color",
      colorExpression
    );

    // Reset the point stroke to the value-based default (white, yellow when
    // selected), clearing any per-category stroke left behind by Custom Ordinal.
    map.setPaintProperty(sublayers.unclustered, "circle-stroke-color", [
      "case",
      ["boolean", ["feature-state", "selected"], false],
      "#FFCF01",
      "#ffffff"
    ] as unknown as ExpressionSpecification);
  }

  // Handle line layers
  if (sublayers.stroke && map.getLayer(sublayers.stroke)) {
    map.setPaintProperty(sublayers.stroke, "line-color", colorExpression);
  }
};

export const changeOpacityLayer = (
  map: MaplibreMap | null,
  sublayers: LayerSublayers,
  opacity: number
) => {
  if (!map) return;

  // Handle polygon layers
  if (
    sublayers.fill &&
    sublayers.outline &&
    map.getLayer(sublayers.fill) &&
    map.getLayer(sublayers.outline)
  ) {
    map.setPaintProperty(sublayers.fill, "fill-opacity", opacity);
    map.setPaintProperty(sublayers.outline, "line-opacity", opacity);
  }

  // Handle point layers
  if (
    sublayers.circle &&
    sublayers.unclustered &&
    map.getLayer(sublayers.circle) &&
    map.getLayer(sublayers.unclustered)
  ) {
    // Image mode = the symbol icon layer is visible; the dot stays hidden and
    // the image marker carries the opacity instead.
    const iconVisible =
      !!sublayers.icon &&
      !!map.getLayer(sublayers.icon) &&
      map.getLayoutProperty?.(sublayers.icon, "visibility") === "visible";

    // Clusters always follow the slider.
    map.setPaintProperty(sublayers.circle, "circle-opacity", opacity);
    map.setPaintProperty(sublayers.circle, "circle-stroke-opacity", opacity);

    // Update unclustered points (keep the dot hidden behind the image marker).
    if (iconVisible) {
      map.setPaintProperty(sublayers.unclustered, "circle-opacity", 0);
    } else {
      map.setPaintProperty(sublayers.unclustered, "circle-opacity", opacity);
      map.setPaintProperty(
        sublayers.unclustered,
        "circle-stroke-opacity",
        opacity
      );
    }
  }

  // Image marker (symbol icon) opacity follows the slider.
  if (sublayers.icon && map.getLayer(sublayers.icon)) {
    map.setPaintProperty(sublayers.icon, "icon-opacity", opacity);
  }

  // Handle line layers
  if (sublayers.stroke && map.getLayer(sublayers.stroke)) {
    map.setPaintProperty(sublayers.stroke, "line-opacity", opacity);
  }
};

export const changeStrokeWidthLayer = (
  map: MaplibreMap | null,
  sublayers: LayerSublayers,
  strokeWidth: number
) => {
  if (!map) return;

  if (sublayers.stroke && map.getLayer(sublayers.stroke)) {
    map.setPaintProperty(sublayers.stroke, "line-width", strokeWidth);
  }

  if (sublayers.outlineSelected && map.getLayer(sublayers.outlineSelected)) {
    map.setPaintProperty(
      sublayers.outlineSelected,
      "line-gap-width",
      strokeWidth
    );
  }
};

export const removeAllSourceLayerWithPrefix = async (
  map: MaplibreMap | null,
  prefix: string,
  ignoreTextList: string[]
): Promise<void> => {
  if (!map) return;
  const layerIdsToRemove = map
    ?.getStyle()
    ?.layers?.filter((layer) => layer.id.startsWith(prefix))
    ?.map((layer) => layer.id);

  layerIdsToRemove?.forEach((layerId) => {
    map.removeLayer(layerId);
  });
  // Get all source start with passing prefix
  const sources = Object.keys(map?.getStyle()?.sources ?? {})?.filter(
    (source) => source.includes(prefix)
  );
  // Filter source ignore text
  const sourceIgnoreText = sources.filter(
    (source) => !ignoreTextList.some((text) => source.includes(text))
  );
  sourceIgnoreText.forEach((source) => {
    if (map.getSource(source)) map.removeSource(source);
  });
};

export const getSourceProperties = (
  map: MaplibreMap | null,
  sourceId: string
) => {
  if (!map) return;

  // We use startsWith() to match source IDs because in @useSiteProfilingLayers.ts we append suffixes
  // like '-polygon' to create custom layer IDs. This ensures we find the base source ID regardless
  // of any suffixes that might have been added.
  const matchingSourceId = Object.keys(map.getStyle().sources).find(
    (sourceKey) => sourceKey.startsWith(sourceId)
  );

  if (!matchingSourceId) return;

  const source = map.getSource(matchingSourceId);

  if (!source) return;

  const data = (source as maplibregl.GeoJSONSource)._data;
  if (typeof data === "string" || !data || !("features" in data)) return [];

  return data;
};

/**
 * Resolves when a GeoJSON source with the given ID (or a source whose ID starts with it)
 * is added to the map and fully loaded.
 * If the source is already present, resolves immediately.
 * Falls back to resolving after `timeoutMs` to avoid permanently leaking the listener
 * when the source never becomes ready (e.g. removed before load completes).
 */
export const waitForSource = (
  map: MaplibreMap,
  sourceId: string,
  timeoutMs = 10_000
): Promise<void> => {
  const isReady = () => {
    const matchingId = Object.keys(map.getStyle().sources).find((key) =>
      key.startsWith(sourceId)
    );
    return !!matchingId && map.isSourceLoaded(matchingId);
  };

  if (isReady()) return Promise.resolve();

  return new Promise((resolve) => {
    const onSourceData = (e: maplibregl.MapSourceDataEvent) => {
      if (!e.sourceId.startsWith(sourceId) || !e.isSourceLoaded) return;
      clearTimeout(timer);
      map.off("sourcedata", onSourceData);
      resolve();
    };

    const timer = setTimeout(() => {
      map.off("sourcedata", onSourceData);
      console.warn(
        `[waitForSource] timed out waiting for source "${sourceId}"`
      );
      resolve();
    }, timeoutMs);

    map.on("sourcedata", onSourceData);
  });
};

/**
 * Guards add operations against a map whose style isn't ready.
 *
 * `map.style` instance in MapLibre holds the style information
 * for the map, including layers, sources, and other style properties.
 * So, make sure the style is loaded before performing any operations that depend on it.
 *
 * Call `retry` function to re-attempt the operation once the style is loaded.
 */
export const ensureMapStyleReady = (
  map: MaplibreMap | null | undefined,
  retry: () => void
): map is MaplibreMap => {
  if (!map) return false;
  if (map.isStyleLoaded()) return true;

  map.once("styledata", retry);
  return false;
};

export const addLayer = ({
  isNewUIDatasetConfigEnabled,
  isRankingLabelEnabled,
  map,
  layerData,
  style,
  selectedCategory,
  isOrdinal,
  addOrdinalFunc,
  addQuantileFunc
}: {
  isNewUIDatasetConfigEnabled: boolean;
  isRankingLabelEnabled: boolean;
  map: MaplibreMap;
  layerData: Omit<FeatureDataConfig, "data"> & { id: string };
  style: DatasetStyle;
  selectedCategory: string;
  isOrdinal: boolean;
  addOrdinalFunc: (
    isNewUIDatasetConfigEnabled: boolean,
    isRankingLabelEnabled: boolean,
    map: MaplibreMap,
    layer: Omit<FeatureDataConfig, "data"> & { id: string },
    style: DatasetStyle,
    category: string
  ) => void;
  addQuantileFunc: (
    isNewUIDatasetConfigEnabled: boolean,
    isRankingLabelEnabled: boolean,
    map: MaplibreMap,
    dataset: Omit<FeatureDataConfig, "data"> & { id: string },
    style: DatasetStyle,
    category: string
  ) => void;
}) => {
  const addLayerMethods = {
    ordinal: () =>
      addOrdinalFunc(
        isNewUIDatasetConfigEnabled,
        isRankingLabelEnabled,
        map,
        layerData,
        style,
        selectedCategory
      ),
    quantile: () =>
      addQuantileFunc(
        isNewUIDatasetConfigEnabled,
        isRankingLabelEnabled,
        map,
        layerData,
        style,
        selectedCategory
      )
  };

  const method = isOrdinal ? "ordinal" : "quantile";
  addLayerMethods[method]();
};

export const getRadiusPolygon = (
  center: [number, number],
  radius: number
): Feature<Geometry, GeoJsonProperties> => {
  const circle = turf.circle(center, radius, {
    steps: 64,
    units: "meters"
  });
  return circle;
};

export const hideAllLayersWithPrefixes = (
  map: MaplibreMap,
  prefixes: string[]
) => {
  const hiddenLayerIds: string[] = [];

  map.getStyle().layers.forEach((layer) => {
    if (prefixes.some((prefix) => layer.id.startsWith(prefix))) {
      const visibility = map.getLayoutProperty(layer.id, "visibility");
      if (visibility !== "none") {
        map.setLayoutProperty(layer.id, "visibility", "none");
        hiddenLayerIds.push(layer.id);
      }
    }
  });
  return hiddenLayerIds;
};

export const showLayersWithLayerIds = (
  map: MaplibreMap,
  layerIds: string[]
) => {
  const combinedFeatures: Feature<Geometry, GeoJsonProperties>[] = [];

  layerIds.forEach((layerId) => {
    // Ids were captured when the layers were hidden; the layers can be removed
    // or rebuilt before restore (e.g. datasets refreshed while the search
    // location popup is open), and styling a stale id raises a map error.
    const layer = map.getLayer(layerId);
    if (!layer) return;
    map.setLayoutProperty(layerId, "visibility", "visible");
    if (layer?.source) {
      const source = map.getSource(layer.source);
      if (source && (source as any).type === "geojson") {
        const geojsonSource = source as {
          _data?: FeatureCollection<Geometry, GeoJsonProperties>;
          _options?: { data?: FeatureCollection<Geometry, GeoJsonProperties> };
        };
        const data = geojsonSource._data || geojsonSource._options?.data;
        if (data && Array.isArray(data.features)) {
          combinedFeatures.push(...data.features);
        }
      }
    }
  });

  if (combinedFeatures.length > 0) {
    const geojson: FeatureCollection<Geometry, GeoJsonProperties> = {
      type: "FeatureCollection",
      features: combinedFeatures
    };
    const bounds = turf.bbox(geojson); // [minX, minY, maxX, maxY]
    map.fitBounds(
      [
        [bounds[0], bounds[1]],
        [bounds[2], bounds[3]]
      ],
      {
        padding: BOUNDS_PADDING
      }
    );
  }
};

export const reorderLayerBasedOnArea = (
  features: FeatureCollection<Geometry>
): FeatureCollection<Geometry> => {
  const sortedFeatures = [...features.features].sort((a, b) => {
    const areaA = turf.area(a);
    const areaB = turf.area(b);
    return areaB - areaA;
  });

  return {
    ...features,
    features: sortedFeatures
  };
};

/**
 * Gets the ID of the item that appears immediately before the target in a given list.
 *
 * @param targetId - The ID of the target item.
 * @param orders - Optional array of objects containing an `id` and a `type`.
 * @returns A string with the ID and layer type (`circle` for "poi", `fill` otherwise), or `undefined` if not found.
 */
export function getBeforeId(
  targetId: string,
  orders?: { id: string; type: SpatialAggregationType }[]
): string | undefined {
  if (!orders?.length) return undefined;

  const list = orders.map((v) => v.id);
  const index = list.indexOf(targetId);

  if (index === -1 || index === 0) return undefined;

  const previousOrder = orders[index - 1];

  const getLayerSuffix = (type: SpatialAggregationType): string => {
    if (type === "poi") return "circle";
    if (type === "line") return "line-stroke";
    return "fill";
  };

  return `${previousOrder.id}-${getLayerSuffix(previousOrder.type)}`;
}

/**
 * Filters out features whose property values match any of the provided filters.
 *
 * @param features - Array of GeoJSON features.
 * @param field - The property key to filter by.
 * @param filters - Filter conditions (categorical or numerical).
 * @param dataType - The type of data (float, int, string, etc.).
 * @param top9 - An array of top 9 most common string values used for special comparison logic.
 * @returns An array of features that do NOT match any of the filters.
 */
export function filterPointFeatures(
  features: Feature[],
  field: string,
  filters:
    | (string | { min: number | null; max: number | null } | null)[]
    | undefined,
  dataType: DataSchemaType,
  top9: string[]
): Feature[] {
  if (!filters?.length) return features;

  return features.filter(({ properties }) => {
    const value = properties?.[field];
    if (value === null || value === undefined) return true;
    return !isFeatureMatchingFilters(value, filters, dataType, top9);
  });
}

/**
 * Builds the MapLibre filter expression that the legend's hide/show toggles
 * produce for a dataset's non-POINT sublayers. Extracted so that both the
 * legend-toggle path and the time-series scrub path can compose the same
 * expression into a single `["all", ...]` filter — otherwise whichever path
 * runs last would overwrite the other via `map.setFilter`.
 *
 * Returns `null` for POINT datasets (those use feature-level filtering, not
 * `setFilter`) and when there are no active legend filters.
 */
export function buildLegendFilterExpression(
  dataset: Dataset,
  filters:
    | (string | { min: number | null; max: number | null } | null)[]
    | undefined
) {
  const spatialType = dataset.properties?.spatialType;
  if (!spatialType || spatialType === LayerType.POINT) return null;

  const selectedCategory = dataset.selectedCategory;
  if (!selectedCategory) return null;

  const field =
    spatialType === LayerType.LINE
      ? (dataset.style?.stroke?.attribute_name ??
        selectedCategory.attribute_name)
      : selectedCategory.attribute_name;

  const dataType =
    spatialType === LayerType.LINE
      ? (dataset.categories?.find((c) => c.attribute_name === field)
          ?.data_type ?? selectedCategory.data_type)
      : selectedCategory.data_type;

  const colorRanges = dataset.style?.colorRanges ?? [];
  const nonNullRanges = colorRanges.filter(
    (r) => r.value !== "" && r.label?.toLowerCase() !== "null"
  );
  const top9Values = nonNullRanges
    .slice(0, -1)
    .map((r) => r.value)
    .filter((v): v is string => typeof v === "string");

  return generateNonPointMapFilter(field, filters ?? [], dataType, top9Values);
}

/**
 * Generates a Mapbox GL filter expression to exclude features based on filter conditions.
 *
 * @param field - The property key to filter by.
 * @param filters - Filter conditions (categorical or numerical).
 * @param dataType - The type of data (float, int, string, etc.).
 * @param top9 - An array of top 9 most common string values used for special comparison logic.
 * @returns A Mapbox GL filter expression or `null` if no filters apply.
 */
export function generateNonPointMapFilter(
  field: string,
  filters:
    | (string | { min: number | null; max: number | null } | null)[]
    | undefined,
  dataType: DataSchemaType,
  top9: string[]
) {
  if (!filters?.length) return null;

  if ([DataSchemaType.FLOAT, DataSchemaType.INT].includes(dataType)) {
    const rangeFilters = (filters as { min: number; max: number }[]).map(
      (range) => {
        // handle null & 0 value
        if (!range.min && !range.max) {
          return [
            "==",
            ["get", field],
            typeof range.min === "number" ? 0 : null
          ];
        }
        const { min: processedMin, max: processedMax } =
          processNumericRange(range);

        return [
          "all",
          ["!=", ["get", field], null],
          [">=", ["get", field], processedMin],
          ["<=", ["get", field], processedMax]
        ];
      }
    );

    return ["!", ["any", ...rangeFilters]];
  }

  const exclusionFilters = (filters as string[]).map((v) =>
    top9.includes(v)
      ? ["!=", ["get", field], v]
      : ["!", ["match", ["get", field], top9, false, true]]
  );

  return exclusionFilters.length ? ["all", ...exclusionFilters] : null;
}

/**
 * Determines whether a feature's property value matches any of the provided filters.
 *
 * @param value - The value to test.
 * @param filters - Filter conditions (categorical or numerical).
 * @param dataType - The type of data (FLOAT, INT, STRING, etc.).
 * @param top9Values - An array of top 9 values used for special matching logic.
 * @returns `true` if the value matches any filter, `false` otherwise.
 */
export function isFeatureMatchingFilters(
  value: string | number,
  filters:
    | (string | { min: number | null; max: number | null } | null)[]
    | undefined,
  dataType: DataSchemaType,
  top9Values: string[]
): boolean {
  if (!filters?.length) return false;

  if ([DataSchemaType.FLOAT, DataSchemaType.INT].includes(dataType)) {
    if (typeof value === "number") {
      return filters.some((f) => {
        if (typeof f === "object" && f !== null) {
          const { min: processedMin, max: processedMax } =
            processNumericRange(f);
          return value >= processedMin && value <= processedMax;
        }
        return false;
      });
    } else if (!value) {
      return filters.some((f) => f === "null");
    }
  } else if (typeof value === "boolean") {
    let boolValue: string;
    if (value === true) {
      boolValue = "true";
    } else if (value === false) {
      boolValue = "false";
    } else {
      boolValue = "null";
    }
    return filters.includes(boolValue);
  }

  return filters.some((f) => {
    if (typeof f !== "string") return false;
    if (f === "") return value === "";
    return top9Values.includes(f)
      ? value === f
      : !top9Values.includes(value as string) && value !== ""; // this is for "others"
  });
}

/**
 * Resolves a Custom Ordinal legend hide-list (category labels + the "Other"
 * sentinel) into the actual feature values to hide.
 */
function customOrdinalHiddenSets(
  config: CustomOrdinalValue,
  filters:
    | (string | { min: number | null; max: number | null } | null)[]
    | undefined
) {
  const hiddenLabels = new Set(
    (filters ?? []).filter((f): f is string => typeof f === "string")
  );
  const lookup = config.valueByDisplayName ?? {};
  // A feature may carry the display name or the raw value — include both.
  const expand = (values: string[]) =>
    values.flatMap((v) => {
      const raw = lookup[v];
      return raw != null && raw !== v ? [v, raw] : [v];
    });

  const hiddenValues = new Set<string>();
  const allAssigned = new Set<string>();
  config.steps.forEach((step) => {
    const expanded = expand(step.values);
    expanded.forEach((v) => allAssigned.add(v));
    if (hiddenLabels.has(step.label)) {
      expanded.forEach((v) => hiddenValues.add(v));
    }
  });

  return {
    hiddenValues,
    allAssigned,
    otherHidden: hiddenLabels.has(CUSTOM_ORDINAL_OTHER_LEGEND_KEY),
    hasAny: hiddenLabels.size > 0
  };
}

/** Whether a Custom Ordinal feature value is hidden by the legend toggles. */
export function isCustomOrdinalFeatureHidden(
  value: string | number | null | undefined,
  config: CustomOrdinalValue,
  filters:
    | (string | { min: number | null; max: number | null } | null)[]
    | undefined
): boolean {
  if (value === null || value === undefined) return false;
  const { hiddenValues, allAssigned, otherHidden } = customOrdinalHiddenSets(
    config,
    filters
  );
  const v = String(value);
  return hiddenValues.has(v) || (otherHidden && !allAssigned.has(v));
}

/**
 * Keeps only the Custom Ordinal point features that are not hidden by the
 * legend's per-category show/hide toggles (a category hides its assigned
 * values; "Other" hides everything not assigned to a category).
 */
export function filterPointFeaturesCustomOrdinal(
  features: Feature[],
  field: string,
  config: CustomOrdinalValue,
  filters:
    | (string | { min: number | null; max: number | null } | null)[]
    | undefined
): Feature[] {
  const { hiddenValues, allAssigned, otherHidden, hasAny } =
    customOrdinalHiddenSets(config, filters);
  if (!hasAny) return features;

  return features.filter(({ properties }) => {
    const raw = properties?.[field];
    if (raw === null || raw === undefined) return true;
    const value = String(raw);
    const hidden =
      hiddenValues.has(value) || (otherHidden && !allAssigned.has(value));
    return !hidden;
  });
}

/**
 * Builds the MapLibre `setFilter` expression for a Custom Ordinal non-point
 * layer from the legend's per-category show/hide toggles. Returns `null` when
 * nothing is hidden.
 */
export function generateCustomOrdinalLegendFilter(
  field: string,
  config: CustomOrdinalValue,
  filters:
    | (string | { min: number | null; max: number | null } | null)[]
    | undefined
) {
  const { hiddenValues, allAssigned, otherHidden, hasAny } =
    customOrdinalHiddenSets(config, filters);
  if (!hasAny) return null;

  // Coerce the feature value to string so numeric columns match the string
  // category values (same as the color expression).
  const field_ = ["to-string", ["get", field]];
  const conditions: unknown[] = [];
  const hidden = [...hiddenValues];
  if (hidden.length) {
    conditions.push(["match", field_, hidden, true, false]);
  }
  if (otherHidden) {
    const assigned = [...allAssigned];
    // "Other" = values not assigned to any category. With nothing assigned,
    // every feature is "Other", so hide all.
    conditions.push(
      assigned.length ? ["!", ["match", field_, assigned, true, false]] : true
    );
  }
  if (!conditions.length) return null;

  // Show a feature when it matches NONE of the hide conditions.
  return ["!", ["any", ...conditions]];
}

/** Whether a numeric value falls inside a break range: `> start` and `≤ end`. */
const isValueInBreakRange = (
  value: number,
  step: { start: number | null; end: number | null }
): boolean =>
  (step.start === null || value > step.start) &&
  (step.end === null || value <= step.end);

/**
 * Resolves the Custom Breaks legend hide-list into the hidden break ranges. The
 * legend stores hidden row keys (`break-{index}` and the sentinel
 * `break-other`); this maps them back to numeric ranges so the show/hide toggles
 * act on the right features.
 */
function customBreaksHiddenSets(
  config: CustomBreaksValue,
  filters:
    | (string | { min: number | null; max: number | null } | null)[]
    | undefined
) {
  const hiddenKeys = new Set(
    (filters ?? []).filter((f): f is string => typeof f === "string")
  );
  const hiddenRanges = config.steps.filter((_, index) =>
    hiddenKeys.has(`break-${index}`)
  );
  return {
    hiddenRanges,
    otherHidden: hiddenKeys.has("break-other"),
    hasAny: hiddenKeys.size > 0
  };
}

/** Whether a Custom Breaks feature value is hidden by the legend toggles. */
export function isCustomBreaksFeatureHidden(
  value: string | number | null | undefined,
  config: CustomBreaksValue,
  filters:
    | (string | { min: number | null; max: number | null } | null)[]
    | undefined
): boolean {
  const { hiddenRanges, otherHidden } = customBreaksHiddenSets(config, filters);
  const n = typeof value === "number" ? value : Number(value);
  // Missing / non-numeric values fall into "Other".
  if (
    value === null ||
    value === undefined ||
    value === "" ||
    Number.isNaN(n)
  ) {
    return otherHidden;
  }
  if (hiddenRanges.some((step) => isValueInBreakRange(n, step))) return true;
  if (otherHidden && !config.steps.some((step) => isValueInBreakRange(n, step)))
    return true;
  return false;
}

/**
 * Keeps only the Custom Breaks point features that are not hidden by the legend's
 * per-range show/hide toggles (a range hides values that fall into it; "Other"
 * hides values in a gap / outside every range).
 */
export function filterPointFeaturesCustomBreaks(
  features: Feature[],
  field: string,
  config: CustomBreaksValue,
  filters:
    | (string | { min: number | null; max: number | null } | null)[]
    | undefined
): Feature[] {
  const { hasAny } = customBreaksHiddenSets(config, filters);
  if (!hasAny) return features;
  return features.filter(
    ({ properties }) =>
      !isCustomBreaksFeatureHidden(properties?.[field], config, filters)
  );
}

/**
 * Builds the MapLibre `setFilter` expression for a Custom Breaks non-point layer
 * from the legend's per-range show/hide toggles. Returns `null` when nothing is
 * hidden.
 */
export function generateCustomBreaksLegendFilter(
  field: string,
  config: CustomBreaksValue,
  filters:
    | (string | { min: number | null; max: number | null } | null)[]
    | undefined
) {
  const { hiddenRanges, otherHidden, hasAny } = customBreaksHiddenSets(
    config,
    filters
  );
  if (!hasAny) return null;

  const conditions: unknown[] = hiddenRanges.map((step) =>
    buildBreakCondition(step, field)
  );
  if (otherHidden) {
    // "Other" = value not in ANY defined range. With no ranges, hide all.
    const anyRange = config.steps.map((step) =>
      buildBreakCondition(step, field)
    );
    conditions.push(anyRange.length ? ["!", ["any", ...anyRange]] : true);
  }
  if (!conditions.length) return null;

  // Show a feature when it matches NONE of the hide conditions.
  return ["!", ["any", ...conditions]];
}

/**
 * Truncate a GeoJSON Feature (Point or Polygon) to fixed decimal precision.
 *
 * Supports: Point, Polygon
 *
 * @param feature - GeoJSON Feature<Geometry, GeoJsonProperties>
 * @param precision - Decimal precision (default: 6)
 * @param coordinateDimensions - Number of coordinate dimensions (default: 2)
 * @param mutate - Whether to mutate the input geometry (default: false)
 * @returns Truncated GeoJSON Feature<Geometry, GeoJsonProperties>
 */
export const truncateGeoJson = <T extends Geometry>(
  feature: Feature<T, GeoJsonProperties>,
  precision = DEFAULT_GEOM_PRECISION,
  coordinateDimensions = 2,
  mutate = false
): Feature<T, GeoJsonProperties> => {
  if (!feature?.geometry || !("coordinates" in feature.geometry)) {
    return feature;
  }

  const truncatedFeature = turf.truncate(feature, {
    precision,
    coordinates: coordinateDimensions,
    mutate
  });

  return {
    ...feature,
    geometry: truncatedFeature.geometry as T
  };
};

export const transformStyle = (
  previousStyle?: StyleSpecification,
  nextStyle?: StyleSpecification
): StyleSpecification => {
  // Projection from mapbox causing error
  delete nextStyle?.projection;

  if (!previousStyle && !nextStyle) {
    throw new Error("transformStyle received no styles to merge");
  }

  if (!nextStyle) return previousStyle!;
  if (!previousStyle) return nextStyle;

  const prev = previousStyle;
  const next = nextStyle;

  const isCartoId = (id: string) => id.toLowerCase().includes("carto");
  const isCompositeId = (id: string) => id.toLowerCase().includes("composite");
  const isMapboxDraw = (id: string) => id.includes("mapbox-gl-draw");
  const isMapboxSatellite = (id: string) =>
    id.toLowerCase().includes("mapbox-satellite");

  const filteredPreviousSources = Object.fromEntries(
    Object.entries(prev.sources ?? {}).filter(
      ([id]) =>
        !isCartoId(id) &&
        !isCompositeId(id) &&
        !isMapboxDraw(id) &&
        !isMapboxSatellite(id)
    )
  );

  const mergedSources = {
    ...filteredPreviousSources,
    ...Object.fromEntries(
      Object.entries(next.sources ?? {}).map(([id, source]) => [id, source])
    )
  };

  const processedNextLayers: LayerSpecification[] = (next.layers ?? []).map(
    (layer) => layer
  );

  const mergedLayers = [
    ...processedNextLayers,
    ...(prev.layers ?? []).filter((layer) => {
      if (layer.type === "background") return false;

      if (
        layer?.source === "carto" ||
        layer?.source === "composite" ||
        layer?.source.includes("mapbox-gl-draw") ||
        layer?.source === "mapbox-satellite"
      )
        return false;

      return true;
    })
  ];

  return {
    ...next,
    sources: mergedSources,
    layers: mergedLayers
  };
};

export const changeLabelLayerVisibility = (
  map: MaplibreMap | null,
  layerId: string,
  visibility: boolean
) => {
  if (!map) return;

  const style = map.getStyle();
  if (!style.layers) return;

  // Ensure the label layer exists
  const labelLayer = style.layers.find((l) => l.id === layerId);
  if (!labelLayer) return;

  // Update label layer visibility
  map.setLayoutProperty(layerId, "visibility", visibility ? "visible" : "none");

  // Calculate the corresponding offset for the rank layer
  const labelRankLayerId = layerId.replace("label", "rank");
  const rankOffset = calculateRankOffset(map, layerId, visibility);

  // Update rank layer offset
  const rankLayer = style.layers.find((l) => l.id === labelRankLayerId);
  if (rankLayer) {
    map.setLayoutProperty(labelRankLayerId, "text-offset", rankOffset);
  }
};

export const changeCategoryLabelLayer = (
  map: MaplibreMap | null,
  layerId: string,
  category?: DatasetAttributeSchema
) => {
  if (!map) return;

  const style = map.getStyle();

  if (!style.layers) return;

  map.setLayoutProperty(layerId, "text-field", [
    "to-string",
    ["get", category?.attribute_name]
  ]);
};

export const changeLabelLayerColor = (
  map: MaplibreMap | null,
  layerId: string,
  selectedColor: EColorLabel
) => {
  if (!map) return;

  const style = map.getStyle();
  if (!style.layers) return;

  const colorLabel = COLOR_LABEL[selectedColor];

  map.setPaintProperty(layerId, "text-color", colorLabel.COLOR);
  map.setPaintProperty(layerId, "text-halo-color", colorLabel.HALO_COLOR);
};

export const changeLabelLayerOpacity = (
  map: MaplibreMap | null,
  layerId: string,
  opacity: number
) => {
  if (!map) return;

  const style = map.getStyle();
  if (!style.layers) return;

  map.setPaintProperty(layerId, "text-opacity", opacity);
};

export const calculateRankOffset = (
  map: MaplibreMap,
  labelLayerId: string,
  isLabelVisible: boolean
): [number, number] => {
  const labelOffset = map.getLayoutProperty(labelLayerId, "text-offset");
  const baseOffsetY = labelOffset?.[1] ?? 0;
  const nextOffsetY = isLabelVisible ? baseOffsetY - 2 : baseOffsetY;
  return [0, nextOffsetY];
};

export const getTextSizeExpression = (
  isPOI: boolean
): DataDrivenPropertyValueSpecification<number> => [
  "interpolate",
  ["linear"],
  ["zoom"],
  13,
  9, // zoom 13 → 9px
  14,
  12, // zoom 14 → 12px
  ...(isPOI ? [] : [16, 16]) // zoom 16 → 16px for polygon
];

export const getLabelOffset = (isPOI: boolean, isSiteProfiling: boolean) => {
  const base = isPOI ? -1 : 0;
  return isSiteProfiling ? base + 3 : base;
};

export const getRankOffset = (
  showLabel: boolean,
  isPOI: boolean,
  isSiteProfiling: boolean
) => {
  const baseWithLabel = isPOI ? -3 : -2;
  const baseWithoutLabel = isPOI ? -1 : 0;

  const base = showLabel ? baseWithLabel : baseWithoutLabel;

  return isSiteProfiling ? base + 3 : base;
};
